import { useMemo } from 'react'

/**
 * Geometry that drives the blue SelectionHalo.
 *
 * Map-click selections carry a `_geometry` (a stable reference captured at click
 * time in onEachFeature). Detail-navigated selections (e.g. a meter opened from
 * the transformer panel) carry none, so we fall back to the detail entity's
 * reported coordinates — this makes the halo appear even when the Meters layer
 * is off. Fresh search picks are covered by the orange SearchHighlightOverlay,
 * so the fallback is skipped while that ring is up to avoid stacking two rings
 * on one point.
 *
 * Memoized on the coordinate PRIMITIVES (lat/lng), not the detailHighlight
 * object: the fallback path previously built a fresh `{ type: 'Point', ... }`
 * literal on EVERY render, so SelectionHalo's effect (deps [geometry, map]) tore
 * down its layerGroup and restarted its pulse on every re-render — e.g. each
 * mousemove routed through onMouseCoords. Keying the memo on lat/lng returns the
 * SAME reference across renders that do not move the point, so the halo persists
 * stably while the mouse moves.
 */
export function useSelectedGeometry(
  clickGeometry: GeoJSON.Geometry | null,
  detailHighlight: { lat: number; lng: number } | null,
  hasSearchHighlight: boolean,
): GeoJSON.Geometry | null {
  const lat = detailHighlight?.lat ?? null
  const lng = detailHighlight?.lng ?? null
  return useMemo<GeoJSON.Geometry | null>(() => {
    if (clickGeometry) return clickGeometry
    if (lat != null && lng != null && !hasSearchHighlight) {
      return { type: 'Point', coordinates: [lng, lat] }
    }
    return null
  }, [clickGeometry, lat, lng, hasSearchHighlight])
}
